import { Link } from 'react-router-dom';

import { useAcervo } from '@/components/v2/home/useHomeData';
import MotionRoot from '@/components/v2/primitives/MotionRoot';
import Reveal, {
  RevealItem,
  RevealLista,
} from '@/components/v2/primitives/Reveal';
import { LINK_COMUNIDADE_WHATSAPP } from '@/constants/links';
import { HUE_MARCA } from '@/design/hues';

import { COMO_FUNCIONA, EQUIPE, INCLUSO } from './equipe';

/**
 * Números do acervo.
 *
 * Só entram os que dá para contar de verdade: o total de obras e o ano
 * da primeira. Quantidade de encontros e de leitoras exigiria endpoint
 * próprio — enquanto não existir, é melhor não exibir do que exibir
 * chute, que foi exatamente o problema do Math.random() antigo.
 */
const useNumeros = () => {
  const { livros, carregando } = useAcervo();
  if (carregando || !livros.length) return null;

  const anos = livros
    .map((l) => new Date(l.month).getUTCFullYear())
    .filter((a) => !Number.isNaN(a));

  return {
    obras: livros.length,
    desde: anos.length ? Math.min(...anos) : null,
  };
};

export const SobreV2 = () => {
  const numeros = useNumeros();

  return (
    <MotionRoot>
      <div
        data-hue={HUE_MARCA}
        className="min-h-screen bg-background text-on-surface selection:bg-primary-fixed selection:text-on-primary-fixed"
      >
        <header className="banda-creme relative overflow-hidden py-14 md:py-20">
          

          <div className="mx-auto w-full max-w-[1100px] px-4 md:px-5">
            <p className="type-label mb-3.5 flex items-center gap-2.5 text-[var(--banda-acento)]">
              <span
                className="h-[3px] w-[30px] shrink-0 bg-[var(--banda-acento)]"
                aria-hidden="true"
              />
              Sobre o clube
            </p>
            <h1 className="type-club mb-3.5 text-[clamp(36px,8vw,82px)]">
              Um clube de leitura{' '}
              <span className="text-[var(--hue-display)]">
                feito para mulheres
              </span>
            </h1>
            <p className="max-w-[52ch] text-[clamp(15.5px,1.8vw,19px)] text-on-surface-variant">
              Uma obra por mês, escolhida pelas participantes, e um encontro
              presencial em Manaus para discutir com quem leu junto.
            </p>

            <dl className="mt-7 grid grid-cols-1 border-t border-outline-variant sm:grid-cols-2 lg:grid-cols-4">
              {INCLUSO.map((item) => (
                <div
                  key={item.titulo}
                  className="border-b border-outline-variant py-4 pr-5"
                >
                  <dt className="type-club mb-0.5 text-[13.5px]">
                    {item.titulo}
                  </dt>
                  <dd className="m-0 text-[12.5px] text-secondary">
                    {item.detalhe}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </header>

        {numeros && (
          <Reveal className="mx-auto w-full max-w-[1100px] px-4 py-12 md:px-5 md:py-14">
            <dl className="grid grid-cols-2 border-t-2 border-[var(--hue)] md:grid-cols-4">
              <div className="border-b border-outline-variant py-5 pr-5">
                <dd className="type-club type-data m-0 text-[clamp(34px,5vw,56px)] leading-[.88] text-[var(--hue-display)]">
                  {numeros.obras}
                </dd>
                <dt className="type-label text-[10.5px] text-secondary">
                  obras lidas
                </dt>
              </div>
              {numeros.desde && (
                <div className="border-b border-outline-variant py-5 pr-5">
                  <dd className="type-club type-data m-0 text-[clamp(34px,5vw,56px)] leading-[.88] text-[var(--hue-display)]">
                    {numeros.desde}
                  </dd>
                  <dt className="type-label text-[10.5px] text-secondary">
                    desde
                  </dt>
                </div>
              )}
            </dl>
          </Reveal>
        )}

        <section className="mx-auto w-full max-w-[1100px] px-4 py-12 md:px-5 md:py-16">
          <Reveal>
            <h2 className="type-club mb-2 text-[clamp(26px,4.6vw,46px)]">
              Como <span className="text-[var(--hue-display)]">funciona</span>
            </h2>
            <p className="mb-7 max-w-[56ch] text-[15.5px] text-on-surface-variant">
              Do voto na comunidade até a noite do encontro.
            </p>
          </Reveal>

          <RevealLista className="grid grid-cols-1 md:grid-cols-3">
            {COMO_FUNCIONA.map((passo, i) => (
              <RevealItem
                key={passo.titulo}
                className="border-b border-outline-variant py-6 pr-0 md:border-b-0 md:border-r md:pr-6 md:last:border-r-0"
              >
                <span className="type-club mb-2.5 block text-[13px] tracking-[0.12em] text-[var(--banda-acento)]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="type-club mb-1.5 text-[clamp(16px,2vw,21px)]">
                  {passo.titulo}
                </h3>
                <p className="m-0 max-w-[36ch] text-[14.5px] text-on-surface-variant">
                  {passo.texto}
                </p>
              </RevealItem>
            ))}
          </RevealLista>
        </section>

        <section className="mx-auto w-full max-w-[1100px] px-4 py-12 md:px-5 md:py-16">
          <Reveal>
            <h2 className="type-club mb-2 text-[clamp(26px,4.6vw,46px)]">
              Quem <span className="text-[var(--hue-display)]">faz</span>
            </h2>
            <p className="mb-7 max-w-[56ch] text-[15.5px] text-on-surface-variant">
              Quatro estudantes de Manaus que resolveram transformar leitura em
              encontro.
            </p>
          </Reveal>

          <RevealLista className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {EQUIPE.map((pessoa) => (
              <RevealItem key={pessoa.id}>
                <div data-hue={pessoa.hue} className="group">
                  <div
                    className="bg-white p-2 pb-3.5 shadow-[0_20px_40px_-20px_color-mix(in_srgb,var(--hue)_58%,rgba(0,0,0,.5))] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-2 group-hover:rotate-0"
                    style={{ transform: `rotate(${pessoa.inclinacao}deg)` }}
                  >
                    <img
                      src={pessoa.foto}
                      alt={pessoa.nome}
                      className="block aspect-[43/56] w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <span className="mt-3.5 inline-block bg-[var(--hue-fill)] px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-white">
                    {pessoa.cargo}
                  </span>
                  <p className="type-club mb-2 mt-2.5 text-[clamp(17px,2vw,22px)]">
                    {pessoa.nome}
                  </p>
                  <p className="m-0 text-sm text-on-surface-variant">
                    {pessoa.bio}
                  </p>
                </div>
              </RevealItem>
            ))}
          </RevealLista>
        </section>

        <div className="mx-auto w-full max-w-[1100px] px-4 pb-16 md:px-5">
          <div className="flex flex-wrap items-center gap-6 border border-[var(--hue-soft)] bg-[var(--hue-wash)] p-6 md:p-9">
            <div>
              <h2 className="type-club mb-1.5 text-[clamp(19px,2.6vw,27px)]">
                Vem para a próxima
              </h2>
              <p className="m-0 max-w-[42ch] text-[14.5px] text-on-surface-variant">
                As inscrições abrem no início de cada mês, e são avisadas
                primeiro na comunidade.
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 md:ml-auto md:w-auto md:flex-row md:items-center">
              <Link
                to="/"
                className="rounded-full bg-[var(--hue-fill)] px-7 py-4 text-center text-[13px] font-bold uppercase tracking-[0.14em] text-white no-underline shadow-[0_6px_22px_-8px_var(--hue)] transition-transform duration-300 hover:-translate-y-0.5"
              >
                Ver a próxima edição
              </Link>
              <a
                href={LINK_COMUNIDADE_WHATSAPP}
                target="_blank"
                rel="noreferrer"
                className="self-center border-b-2 border-[var(--hue)] pb-0.5 text-[13px] font-bold uppercase tracking-[0.14em] no-underline transition-colors hover:text-[var(--banda-acento)]"
              >
                Entrar na comunidade
              </a>
            </div>
          </div>
        </div>
      </div>
    </MotionRoot>
  );
};

export default SobreV2;
